import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
    Calendar, FolderOpen, PlayCircle,
    Loader2, Download, Tag, ChevronRight, Layers, Camera, UploadCloud, User,
    Car, Clock, ChevronDown, ChevronUp, Target, Video, Briefcase, FileText,
    CheckCircle2, AlertTriangle, XCircle, Circle, Trash2, Check, Eraser, Stamp, Image as ImageIcon
} from "lucide-react";
import { Modal } from './Modal';
import { FilesService, IncaricoService, PraticaService, ClientService } from '../services/apiService';
import { useToast } from '../components/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { FileAnalysisResultDto, UserRole } from '../types';
import { mapWithConcurrency, classifyMediaDeleteResponse, summarizeBatchDelete } from '../utils/batchDelete';
import { UploadSummaryModal } from './UploadSummaryModal';
import { PdfThumb } from './PdfThumb';
import { useResponsabileDeleteDirettoAbilitato } from '../utils/incaricoUtils';
import { isBrowserPlayableVideoUrl } from '../utils/videoPlayback';
import { isBrowserImageUrl, isConvertibleImageExt, copiaInAltroFormato } from '../utils/imagePreview';
import { downloadAttachmentUrl } from '../utils/download';

const VIDEO_EXTS_TM = ['mp4', 'mov', 'avi', 'mts', 'm2ts', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg'];
const isVideoMedia = (m: any): boolean => {
    const name = (m?.cleanFilename || m?.name || m?.filename || '').toLowerCase();
    return VIDEO_EXTS_TM.includes(name.split('.').pop() || '') || !!m?.fileType?.toLowerCase().includes('video');
};

const extractUrl = (res: any): string =>
    typeof res === 'string' ? res : (res?.data?.data || res?.data || res?.url || '');

interface Props {
    pratica: any;
    incarichi: any[];
    onRefresh?: () => void;
}

const DEVICE_OPTIONS = ['FOTOCAMERA', 'VIDEOCAMERA', 'SMARTPHONE', 'SPY', 'ALTRO'] as const;
type DeviceOption = typeof DEVICE_OPTIONS[number];
const UFFICIO_SEGMENT = 'DOCUMENTAZIONE UFFICIO';

type UploadStatus = 'pending' | 'uploading' | 'ready' | 'needs_date' | 'error';
type ModalStep = 'select' | 'confirm' | 'uploading' | 'confirm_dates' | 'completed';

interface UploadItem {
    file: File;
    status: UploadStatus;
    tempId?: string;
    detectedDate?: string;
    manualDate: string;
    error?: string;
}

/** Worker pool: esegue tasks con al massimo `limit` esecuzioni in parallelo */
const runConcurrent = async (tasks: (() => Promise<void>)[], limit: number) => {
    let i = 0;
    const worker = async () => {
        while (i < tasks.length) await tasks[i++]();
    };
    await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
};

export const TabMedia: React.FC<Props> = ({ pratica, incarichi, onRefresh }) => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const [previewFile, setPreviewFile] = useState<any | null>(null);
    const [freshPreviewUrl, setFreshPreviewUrl] = useState<string | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    // Il <video> dell'anteprima non è riuscito a riprodurre l'URL (formato/codec).
    const [previewVideoError, setPreviewVideoError] = useState(false);

    const [currentPath, setCurrentPath] = useState<string>('ROOT');
    const pathSegments = useMemo(() => currentPath.split('/'), [currentPath]);
    const [showKmSection, setShowKmSection] = useState(true);

    // Documenti del cliente collegato (immagini o PDF), letti dall'anagrafica e mostrati
    // in "Documentazione Ufficio". Non stanno in pratica.files; l'endpoint è riservato
    // ad admin/responsabile, quindi i collaboratori non li vedono.
    const [clientDocs, setClientDocs] = useState<any[]>([]);
    useEffect(() => {
        const cid = (pratica as any)?.clientId ?? (pratica as any)?.clienteId;
        if (!cid) { setClientDocs([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const res: any = await ClientService.getById(cid);
                const cli: any = res?.data ?? res;
                const files: any[] = cli?.files || [];
                const imgExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp'];
                const vidExts = ['mp4', 'mov', 'avi', 'mts', 'm2ts', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg'];
                const mapped = files
                    .filter((f: any) => f && (f.urlCompressed || f.urlOriginal) && !f.deleted)
                    .map((f: any) => {
                        const ext = (f.extension || (f.name?.includes('.') ? f.name.split('.').pop() : '') || '').toLowerCase();
                        const fileType = imgExts.includes(ext) ? `image/${ext === 'jpg' ? 'jpeg' : ext}`
                            : vidExts.includes(ext) ? `video/${ext}` : (f.fileType || '');
                        const d = new Date(f.dataAcquisizione || f.createdAt);
                        return {
                            ...f, isOffice: true, isClientDoc: true,
                            uploaderName: 'DOCUMENTO CLIENTE',
                            dateObj: d, folderDate: d,
                            categoryName: 'DOCUMENTAZIONE UFFICIO',
                            soggettoNome: UFFICIO_SEGMENT,
                            cleanFilename: (f.name || '').replace(/^DocID_/i, ''),
                            displayUrl: f.urlCompressed || f.urlOriginal || '',
                            fileType,
                        };
                    });
                if (!cancelled) setClientDocs(mapped);
            } catch { if (!cancelled) setClientDocs([]); }
        })();
        return () => { cancelled = true; };
    }, [(pratica as any)?.clientId, (pratica as any)?.clienteId]);

    // Upload
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [modalStep, setModalStep] = useState<ModalStep>('select');
    const [uploadType, setUploadType] = useState<'ufficio' | 'media'>('media');
    const [uploadCategoria, setUploadCategoria] = useState<DeviceOption>('SMARTPHONE');
    const [uploadSoggetto, setUploadSoggetto] = useState<string>('');
    const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
    const [isConfirming, setIsConfirming] = useState(false);
    const [completedSummary, setCompletedSummary] = useState<{ ok: number; discarded: number; errors: number } | null>(null);
    // Scelta "data in sovrimpressione" per il batch; torna a true a ogni nuovo upload.
    const [applyDateWatermark, setApplyDateWatermark] = useState(true);

    // Solo titolare/responsabile gestiscono il watermark (il BE ricontrolla il ruolo).
    const canReprocess = user?.role === UserRole.ADMIN || user?.role === UserRole.RESPONSABILE;
    const canToggleWatermark = canReprocess;
    // Il responsabile elimina direttamente solo se la deroga è attiva in Settings,
    // altrimenti passa per l'approvazione.
    const responsabileDeleteDirettoAbilitato = useResponsabileDeleteDirettoAbilitato();
    // Media in riprocessamento (spinner sul bottone).
    const [watermarkLoadingId, setWatermarkLoadingId] = useState<number | null>(null);
    // Media con polling della pipeline dopo un riprocessamento del watermark: servono
    // per il badge "ELABORAZIONE", perché il vecchio file compresso resta puntato
    // fino al callback e l'euristica sugli URL non basta.
    const [pollingMediaIds, setPollingMediaIds] = useState<Set<number>>(new Set());
    const pollIntervalsRef = useRef<Map<number, number>>(new Map());
    // Ferma tutti i polling allo smontaggio.
    useEffect(() => {
        return () => {
            pollIntervalsRef.current.forEach((id) => window.clearInterval(id));
            pollIntervalsRef.current.clear();
        };
    }, []);

    // Selezione multipla per le azioni batch. Solo i media incarico passano dalla
    // pipeline Python; i file ufficio usano un altro flusso e sono esclusi.
    const [selectedMediaIds, setSelectedMediaIds] = useState<Set<number>>(new Set());
    const [isReprocessing, setIsReprocessing] = useState(false);
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);
    const toggleSelection = (id: number) => {
        setSelectedMediaIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const clearSelection = () => setSelectedMediaIds(new Set());
    // Cambiando cartella si azzera la selezione dei file non più visibili.
    useEffect(() => { clearSelection(); }, [currentPath]);

    // Velocità / avanzamento upload
    const [uploadSpeedMBps, setUploadSpeedMBps] = useState<number | null>(null);
    const [uploadTotalBytes, setUploadTotalBytes] = useState(0);
    const [uploadedBytes, setUploadedBytes] = useState(0);
    const speedWindowRef = useRef<{ t: number; b: number }[]>([]);
    const uploadedBytesRef = useRef(0);

    const addBytesProgress = (bytes: number) => {
        uploadedBytesRef.current += bytes;
        setUploadedBytes(uploadedBytesRef.current);
        const now = Date.now();
        speedWindowRef.current.push({ t: now, b: bytes });
        const cutoff = now - 3000;
        speedWindowRef.current = speedWindowRef.current.filter(x => x.t > cutoff);
        const totalB = speedWindowRef.current.reduce((s, x) => s + x.b, 0);
        const windowMs = now - (speedWindowRef.current[0]?.t ?? now);
        setUploadSpeedMBps(windowMs > 200 ? (totalB / windowMs * 1000) / 1048576 : null);
    };

    // Modale di riepilogo
    const [summaryResults, setSummaryResults] = useState<FileAnalysisResultDto[]>([]);
    const [showSummary, setShowSummary] = useState(false);
    const [summaryIncaricoId, setSummaryIncaricoId] = useState(0);

    const updateItem = (index: number, patch: Partial<UploadItem>) =>
        setUploadItems(prev => prev.map((item, i) => i === index ? { ...item, ...patch } : item));

    const closeModal = () => {
        setIsUploadModalOpen(false);
        setModalStep('select');
        setUploadItems([]);
        setUploadSoggetto('');
        setCompletedSummary(null);
        setUploadSpeedMBps(null);
        setUploadedBytes(0);
        setUploadTotalBytes(0);
        speedWindowRef.current = [];
        uploadedBytesRef.current = 0;
        setApplyDateWatermark(true);
    };

    // La logica batch "Aggiungi/Togli data" è più sotto, dopo viewData,
    // perché serve la lista media della cartella corrente.

    const discardOrphansAndClose = async () => {
        const orphans = uploadItems.filter(i => i.status === 'needs_date' && i.tempId);
        if (orphans.length > 0) {
            await Promise.all(
                orphans.map(i => IncaricoService.deleteMediaByTempId(i.tempId!).catch(() => null))
            );
            addToast(`${orphans.length} file scartati (data mancante)`, 'info');
        }
        closeModal();
        onRefresh?.();
    };

    const closeCompleted = () => {
        closeModal();
        onRefresh?.();
    };

    const soggettiList = useMemo((): { nome: string }[] => {
        try {
            const dati = typeof pratica?.datiAggiuntiviJson === 'string'
                ? JSON.parse(pratica.datiAggiuntiviJson)
                : pratica?.datiAggiuntiviJson || {};
            if (dati.soggetti && Array.isArray(dati.soggetti)) return dati.soggetti;
            if (dati.soggetto_nome || dati.soggetto_denominazione)
                return [{ nome: dati.soggetto_nome || dati.soggetto_denominazione }];
        } catch {}
        return [];
    }, [pratica]);


    useEffect(() => {
        if (previewFile?.id) {
            let mounted = true;
            setIsLoadingPreview(true);
            setFreshPreviewUrl(null);
            setPreviewVideoError(false);
            // Video ufficio: preview=true restituisce la copia mp4 se esiste (il .MTS non è
            // riproducibile nel browser). Anche i PDF chiedono preview: senza, il link è in
            // attachment e l'iframe lo scaricherebbe. Per i media incarico il BE serve già la compressa.
            const previewName = (previewFile.cleanFilename || previewFile.name || '').toLowerCase();
            const previewIsPdf = (previewFile.fileType || '').toLowerCase().includes('pdf') || previewName.endsWith('.pdf');
            const call = previewFile.isOffice
                ? PraticaService.getDownloadUrl(previewFile.id, isVideoMedia(previewFile) || previewIsPdf
                    || isConvertibleImageExt(previewName.split('.').pop()))
                : IncaricoService.getMediaUrl(previewFile.id);
            call
                .then((res: any) => {
                    const url = typeof res === 'string' ? res : (res?.data || res?.url);
                    if (mounted) setFreshPreviewUrl(url || previewFile.displayUrl);
                })
                .catch(() => { if (mounted) setFreshPreviewUrl(previewFile.displayUrl); })
                .finally(() => { if (mounted) setIsLoadingPreview(false); });
            return () => { mounted = false; };
        }
    }, [previewFile]);

    // Avvio upload (concorrenza 4)
    const startUpload = async () => {
        // Con più soggetti va scelto quello di riferimento (solo per i media,
        // la documentazione ufficio non è legata a un soggetto).
        if (uploadType === 'media' && soggettiList.length > 1 && !uploadSoggetto) {
            addToast('Seleziona il soggetto investigato', 'error');
            setModalStep('select');
            return;
        }

        setModalStep('uploading');
        speedWindowRef.current = [];
        uploadedBytesRef.current = 0;
        setUploadSpeedMBps(null);
        setUploadedBytes(0);

        // Cerca un incarico dell'utente coerente col soggetto, altrimenti ne crea uno rapido.
        let targetIncaricoId = 0;
        if (uploadType === 'media') {
            const targetSoggetto = soggettiList.length > 1
                ? uploadSoggetto
                : (soggettiList[0]?.nome || '');
            const mioIncarico = incarichi.find(inc =>
                inc.collaboratore?.id === user?.id
                && (!targetSoggetto || (inc as any).soggettoNome === targetSoggetto)
            );
            targetIncaricoId = mioIncarico?.id ?? 0;
            if (!targetIncaricoId) {
                try {
                    const newRes = await IncaricoService.createIncaricoRapido(
                        pratica.id, 'Upload Ufficio', targetSoggetto || undefined
                    );
                    targetIncaricoId = newRes?.data?.id ?? newRes?.id;
                } catch (err: any) {
                    addToast(`Errore creazione incarico: ${err.message}`, 'error');
                    setModalStep('select');
                    return;
                }
            }
        }

        const snapshot = uploadItems;
        setUploadTotalBytes(snapshot.reduce((s, i) => s + i.file.size, 0));
        const results: UploadStatus[] = new Array(snapshot.length).fill('pending');

        const analysisResults: FileAnalysisResultDto[] = [];

        // Valore del watermark fissato per questo batch.
        const watermarkChoice = applyDateWatermark;

        const tasks = snapshot.map((item, index) => async () => {
            updateItem(index, { status: 'uploading' });
            try {
                let res: any;
                if (uploadType === 'ufficio') {
                    // Documentazione ufficio: niente EXIF, data o watermark, sono documenti amministrativi.
                    // categoria='DOC. UFFICIO' li separa dagli allegati del mandato, così il
                    // collaboratore non li vede nella documentazione fotografica.
                    res = await FilesService.uploadToPratica(pratica.id, item.file, false, 'DOC. UFFICIO');
                    results[index] = 'ready';
                    updateItem(index, { status: 'ready', tempId: res?.tempId });
                    return;
                }
                // Scelta watermark letta alla conferma, prima che closeModal la resetti.
                res = await IncaricoService.uploadMedia(targetIncaricoId, item.file, uploadCategoria, undefined, addBytesProgress, true, watermarkChoice);

                const status: UploadStatus = res?.status === 'needs_date' ? 'needs_date' : 'ready';
                results[index] = status;
                updateItem(index, { status, tempId: res?.tempId, detectedDate: res?.date });
                if (res?.analysisStatus) {
                    analysisResults.push(res as FileAnalysisResultDto);
                }
            } catch (err: any) {
                results[index] = 'error';
                updateItem(index, { status: 'error', error: err?.response?.data?.message || 'Errore upload' });
            }
        });

        await runConcurrent(tasks, 4);

        // Riepilogo analisi, se il backend lo restituisce
        if (analysisResults.length > 0) {
            setSummaryResults(analysisResults);
            setSummaryIncaricoId(targetIncaricoId);
            setShowSummary(true);
            closeModal();
            onRefresh?.();
            return;
        }

        const anyNeedsDate = results.some(r => r === 'needs_date');
        if (anyNeedsDate) {
            setModalStep('confirm_dates');
        } else {
            const ok = results.filter(r => r === 'ready').length;
            const errors = results.filter(r => r === 'error').length;
            setCompletedSummary({ ok, discarded: 0, errors });
            setModalStep('completed');
            onRefresh?.();
        }
    };

    // Conferma date mancanti
    const confirmDates = async () => {
        setIsConfirming(true);
        try {
            const toConfirm = uploadItems.filter(i => i.status === 'needs_date' && i.tempId && i.manualDate);
            const results = await Promise.all(
                toConfirm.map(i => IncaricoService.confirmDate(i.tempId!, i.manualDate))
            );
            // Per un collaboratore il BE crea una richiesta di approvazione invece di salvare
            // la data (direct: false): va mostrato il banner corretto, non "pronto".
            const pending = results.filter((r: any) => r?.data?.direct === false).length;
            const ok = uploadItems.filter(i => i.status === 'ready').length + (toConfirm.length - pending);
            const errors = uploadItems.filter(i => i.status === 'error').length;
            setCompletedSummary({ ok, discarded: 0, errors });
            setModalStep('completed');
            if (pending > 0) {
                addToast(
                    `${pending} ${pending === 1 ? 'data inviata' : 'date inviate'} al titolare per approvazione`,
                    'info'
                );
            }
            onRefresh?.();
        } catch {
            addToast('Errore nella conferma delle date', 'error');
        } finally {
            setIsConfirming(false);
        }
    };

    // Applica o rimuove il watermark rilanciando la pipeline sull'originale.
    // La direzione deve coincidere con l'icona mostrata (stessa regola di showRemove),
    // considerando anche userWantsWatermark perché il flag DB può essere in ritardo.
    // Poi fa polling dello stato fino a COMPLETED, ERROR o al timeout di 15 minuti
    // (i video grandi possono superare i 5 minuti di ricodifica).
    const startWatermarkPolling = (mediaId: number, apply: boolean) => {
        // Se c'è già un polling per lo stesso media lo fermo prima.
        const existing = pollIntervalsRef.current.get(mediaId);
        if (existing != null) window.clearInterval(existing);

        setPollingMediaIds((prev) => {
            const next = new Set(prev);
            next.add(mediaId);
            return next;
        });

        let attempts = 0;
        const MAX_ATTEMPTS = 225; // 225 * 4s = 15 min
        const stop = () => {
            const id = pollIntervalsRef.current.get(mediaId);
            if (id != null) window.clearInterval(id);
            pollIntervalsRef.current.delete(mediaId);
            setPollingMediaIds((prev) => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        };

        const intervalId = window.setInterval(async () => {
            attempts++;
            try {
                const res = await IncaricoService.getMediaProcessingStatus(mediaId);
                const st = res?.status;
                if (st === 'COMPLETED') {
                    stop();
                    addToast(apply ? 'Watermark applicato' : 'Watermark rimosso', 'success');
                    onRefresh?.();
                } else if (st === 'ERROR') {
                    stop();
                    addToast("Elaborazione fallita: il file resta com'era. Riprova.", 'error');
                    // Il server ha riportato la richiesta allo stato reale del file.
                    onRefresh?.();
                } else if (attempts >= MAX_ATTEMPTS) {
                    stop();
                    addToast('Elaborazione ancora in corso. Ricarica la pagina fra qualche minuto.', 'info');
                }
            } catch {
                // Errore di rete: riprovo al prossimo giro.
                if (attempts >= MAX_ATTEMPTS) stop();
            }
        }, 4000);

        pollIntervalsRef.current.set(mediaId, intervalId);
    };

    const handleToggleWatermark = async (media: any) => {
        if (media.isOffice) return; // i file ufficio non passano dalla pipeline media
        const currentlyOn = !!media.watermarkApplied || !!media.userWantsWatermark;
        const apply = !currentlyOn;
        const msg = apply
            ? `Applicare il watermark con la data EXIF a "${media.cleanFilename || media.filename}"?`
            : `Rimuovere il watermark da "${media.cleanFilename || media.filename}"?`;
        if (!window.confirm(msg)) return;

        setWatermarkLoadingId(media.id);
        try {
            await IncaricoService.reprocessWatermark(media.id, apply);
            addToast(apply ? 'Watermark in applicazione' : 'Watermark in rimozione', 'info');
            // Aggiorno subito per il flag ottimistico, poi il polling aggiorna
            // di nuovo quando il nuovo file compresso è pronto.
            onRefresh?.();
            startWatermarkPolling(media.id, apply);
        } catch (err: any) {
            addToast(err?.response?.data?.message || 'Errore durante il ri-processing', 'error');
        } finally {
            setWatermarkLoadingId(null);
        }
    };

    const uploadProgress = useMemo(() => {
        if (uploadItems.length === 0) return 0;
        const done = uploadItems.filter(i => i.status === 'ready' || i.status === 'needs_date' || i.status === 'error').length;
        return Math.round((done / uploadItems.length) * 100);
    }, [uploadItems]);

    // Elenco piatto dei media
    const allMediaFlat = useMemo(() => {
        const list: any[] = [];
        // Nei mandati investigativi si mostra anche il PDF DatiSoggetto (come in FileManagerView),
        // così l'anagrafica del soggetto è a portata di mano. Per gli altri tipi resta nascosto.
        const isInvestigativo = (pratica as any)?.modelId === 'mand-investigativo';
        if (pratica?.files) {
            pratica.files.forEach((f: any) => {
                const nl = f.name.toLowerCase();
                if (!isInvestigativo && nl.startsWith('datisoggetto_')) return;
                if (nl.startsWith('privacy_') || nl.startsWith('docid_')) return;
                if (nl.startsWith('frame_')) return; // frame video catturati nell'editor relazione
                // Anche i file ufficio hanno bisogno di un displayUrl per la miniatura e di un
                // fileType ricavato dall'estensione, perché i Files della pratica non hanno fileType.
                const officeDisplayUrl = f.urlCompressed || f.urlOriginal || '';
                const officeExt = (f.extension
                    || (f.name?.includes('.') ? f.name.split('.').pop() : '') || '').toLowerCase();
                const imgExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp'];
                const vidExts = ['mp4', 'mov', 'avi', 'mts', 'm2ts', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg'];
                const officeFileType = imgExts.includes(officeExt)
                    ? `image/${officeExt === 'jpg' ? 'jpeg' : officeExt}`
                    : vidExts.includes(officeExt) ? `video/${officeExt}`
                    : f.fileType || '';
                const officeDate = new Date(f.dataAcquisizione || f.createdAt);
                list.push({
                    ...f, isOffice: true,
                    uploaderName: f.uploaderName || 'UFFICIO AMMINISTRATIVO',
                    // I file ufficio mantengono la propria data sia per cartella che per display.
                    dateObj: officeDate,
                    folderDate: officeDate,
                    categoryName: f.categoria || 'DOCUMENTAZIONE UFFICIO',
                    soggettoNome: UFFICIO_SEGMENT,
                    cleanFilename: f.name,
                    displayUrl: officeDisplayUrl,
                    fileType: officeFileType,
                });
            });
        }
        // Documenti del cliente collegato (già mappati nello stato clientDocs).
        clientDocs.forEach(d => list.push(d));
        incarichi.forEach(inc => {
            const soggetto = (inc as any).soggettoNome || (soggettiList.length === 1 ? soggettiList[0].nome : 'NON SPECIFICATO');
            // Cartella (Anno/Mese/Giorno), in ordine di priorità:
            // 1) data del report (dataRelazione), se esiste;
            // 2) data di inizio dell'incarico;
            // 3) data reale/EXIF del file (incarichi autogenerati da upload).
            // La data mostrata sul file resta quella reale.
            const reportDateStr: string | undefined = (inc as any).relazioni?.[0]?.dataRelazione;
            const reportDate: Date | null = reportDateStr ? new Date(`${reportDateStr}T12:00:00`) : null;
            const isAutogen = (inc as any).tipoOrigine === 'AUTOGENERATO';
            const startStr: string | undefined = (inc as any).dataInizio;
            const startDate: Date | null = (!isAutogen && startStr) ? new Date(startStr) : null;
            (inc.media || []).forEach((m: any) => {
                if (m.filename?.startsWith('Km_')) return;
                // MANUAL_DATE in attesa: il media non ha ancora la data (si scrive all'approvazione),
                // ma lo mostriamo perché il collaboratore veda il badge "Data in approvazione".
                const isManualDatePending = m.pendingApprovalReason === 'MANUAL_DATE';
                if (!m.dataAcquisizione && !isManualDatePending) return;
                // Nasconde media in attesa di approvazione admin
                if (m.analysisStatus === 'DUPLICATE' || m.analysisStatus === 'OUTSIDE_RANGE' || m.analysisStatus === 'PENDING_APPROVAL') return;
                const displayUrl = m.urlCompressed || m.finalUrl || m.urlOriginal;
                if (!displayUrl) return; // originale non ancora caricato su S3
                const categoryMatch = m.filename?.match(/^\[(.*?)\]/);
                const collaboratorFullName = inc.collaboratore ? `${inc.collaboratore.name} ${inc.collaboratore.surname}` : 'Collaboratore Esterno';
                const realDate = new Date(m.dataAcquisizione || m.uploadTime || m.createdAt);
                list.push({
                    ...m, isOffice: false,
                    uploaderName: m.uploaderName && m.uploaderName !== 'UFFICIO AMMINISTRATIVO' ? m.uploaderName : collaboratorFullName,
                    displayUrl,
                    // dateObj = data reale del file, mostrata sulla card.
                    dateObj: realDate,
                    // folderDate: report → inizio incarico → data reale (vedi sopra).
                    folderDate: reportDate ?? startDate ?? realDate,
                    categoryName: m.categoria || (categoryMatch ? categoryMatch[1].toUpperCase() : 'NON CATEGORIZZATO'),
                    soggettoNome: soggetto,
                    cleanFilename: m.filename?.replace(/^\[.*?\]\s*/, '') || m.filename,
                });
            });
        });
        return list.sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());
    }, [pratica, incarichi, soggettiList, clientDocs]);

    const kmSessions = useMemo(() => {
        // Se lo STOP non viene confermato l'app ritenta, e ogni tentativo carica una nuova
        // foto Km_Finali pur con una sola sessione chiusa. Le sessioni sono quindi delimitate
        // dagli START: lo stop è l'ultima Km_Finali prima del Km_Iniziali successivo.
        // I duplicati restano su S3, qui si filtra solo la vista.
        const result: any[] = [];
        incarichi.forEach(inc => {
            const kmMedia = (inc.media || []).filter((m: any) => m.filename?.startsWith('Km_'));
            if (!kmMedia.length) return;
            const tOf = (m: any) => new Date(m.uploadTime || m.dataAcquisizione).getTime();
            const sort = (arr: any[]) => arr.sort((a: any, b: any) => tOf(a) - tOf(b));
            const iniziali = sort(kmMedia.filter((m: any) => m.filename?.startsWith('Km_Iniziali')));
            const finali = sort(kmMedia.filter((m: any) => m.filename?.startsWith('Km_Finali')));

            let pairs: any[];
            if (iniziali.length === 0) {
                // Nessuno START ma ci sono STOP (anomalo): mostra solo l'ultimo.
                const finale = finali.length ? finali[finali.length - 1] : null;
                pairs = finale ? [{ iniziale: null, finale, dateInizio: null, dateFine: new Date(tOf(finale)) }] : [];
            } else {
                pairs = iniziali.map((start: any, i: number) => {
                    const startT = tOf(start);
                    const nextStartT = i + 1 < iniziali.length ? tOf(iniziali[i + 1]) : Infinity;
                    // Stop della sessione = ultima Km_Finali prima dello START successivo; null se ancora aperta.
                    const inWindow = finali.filter((f: any) => tOf(f) >= startT && tOf(f) < nextStartT);
                    const finale = inWindow.length ? inWindow[inWindow.length - 1] : null;
                    return {
                        iniziale: start, finale,
                        dateInizio: new Date(startT),
                        dateFine: finale ? new Date(tOf(finale)) : null,
                    };
                });
            }
            result.push({ incarico: inc, collaboratoreName: inc.collaboratore ? `${inc.collaboratore.name} ${inc.collaboratore.surname}` : 'Collaboratore', pairs });
        });
        return result;
    }, [incarichi]);

    // Gerarchia: ROOT / [DOC UFFICIO] / Anno / Mese / Giorno / Categoria / Soggetto / Collaboratore → FILES
    const viewData = useMemo(() => {
        const depth = pathSegments.length;
        const isUfficioPath = pathSegments[1] === UFFICIO_SEGMENT;

        if (depth === 1) {
            // Bucket per data-cartella (folderDate = report), non per data reale del file.
            const years = Array.from(new Set(allMediaFlat.filter(m => !m.isOffice).map(m => (m.folderDate ?? m.dateObj).getFullYear().toString()))).sort((a, b) => b.localeCompare(a));
            return { type: 'ROOT' as const, years, hasUfficio: allMediaFlat.some(m => m.isOffice) };
        }
        if (isUfficioPath) return { type: 'FILES' as const, items: allMediaFlat.filter(m => m.isOffice) };

        const filtered = allMediaFlat.filter(m => {
            if (m.isOffice) return false;
            const d = m.folderDate ?? m.dateObj; // bucket sulla data del report
            if (depth > 1 && d.getFullYear().toString() !== pathSegments[1]) return false;
            if (depth > 2 && d.toLocaleString('it-IT', { month: 'long' }).toUpperCase() !== pathSegments[2]) return false;
            if (depth > 3 && d.getDate().toString().padStart(2, '0') !== pathSegments[3]) return false;
            if (depth > 4 && m.categoryName !== pathSegments[4]) return false;
            if (depth > 5 && m.soggettoNome !== pathSegments[5]) return false;
            if (depth > 6 && m.uploaderName !== pathSegments[6]) return false;
            return true;
        });

        if (depth === 2) return { type: 'FOLDERS' as const, items: Array.from(new Set(filtered.map(m => (m.folderDate ?? m.dateObj).toLocaleString('it-IT', { month: 'long' }).toUpperCase()))), icon: <Calendar className="text-blue-500" /> };
        if (depth === 3) return { type: 'FOLDERS' as const, items: Array.from(new Set(filtered.map(m => (m.folderDate ?? m.dateObj).getDate().toString().padStart(2, '0')))).sort(), icon: <Calendar className="text-emerald-500" /> };
        if (depth === 4) return { type: 'FOLDERS' as const, items: Array.from(new Set(filtered.map(m => m.categoryName))), icon: <Tag className="text-amber-500" /> };
        if (depth === 5) return { type: 'FOLDERS' as const, items: Array.from(new Set(filtered.map(m => m.soggettoNome))), icon: <Target className="text-orange-500" /> };
        if (depth === 6) return { type: 'FOLDERS' as const, items: Array.from(new Set(filtered.map(m => m.uploaderName))), icon: <User className="text-slate-500" /> };
        return { type: 'FILES' as const, items: filtered };
    }, [allMediaFlat, pathSegments]);

    const navigateTo = (next: string) => setCurrentPath(prev => `${prev}/${next}`);
    const navigateBackTo = (index: number) => setCurrentPath(pathSegments.slice(0, index + 1).join('/'));

    const FolderCard = ({ name, icon }: { name: string; icon: React.ReactNode }) => (
        <div onClick={() => navigateTo(name)} className="group bg-white border border-slate-200 rounded-2xl p-6 flex flex-col items-center gap-4 cursor-pointer hover:border-blue-400 hover:shadow-lg transition-all">
            <div className="p-4 bg-slate-50 rounded-full group-hover:bg-blue-50 transition-colors">{icon}</div>
            <span className="font-bold text-slate-700 text-xs uppercase tracking-wider text-center">{name}</span>
        </div>
    );

    // Idoneità di ogni media alle azioni batch "Aggiungi/Togli data":
    // - canRemove: timbro già presente o in arrivo (watermarkApplied, o userWantsWatermark con data EXIF);
    // - canAdd: timbro assente e data da EXIF reale (una data manuale non è probante, come nel BE).
    const evalMediaOps = useCallback((media: any): { canAdd: boolean; canRemove: boolean } => {
        if (!canReprocess || media.isOffice) return { canAdd: false, canRemove: false };
        const ext = (media.cleanFilename?.split('.').pop() || '').toLowerCase();
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp'].includes(ext) || media.fileType?.startsWith('image');
        const isVideo = ['mp4', 'mov', 'avi', 'mts', 'm2ts', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg'].includes(ext) || media.fileType?.includes('video');
        if (!isImage && !isVideo) return { canAdd: false, canRemove: false };
        const isProcessing = (!media.urlCompressed && !media.finalUrl) || pollingMediaIds.has(media.id);
        if (isProcessing) return { canAdd: false, canRemove: false };
        // Timbro presente o in arrivo: con una data inserita a mano il timbro non si applica,
        // anche se userWantsWatermark resta attivo, quindi non c'è niente da rimuovere.
        const hasWm = !!media.watermarkApplied || (!!media.userWantsWatermark && !!media.dataAcquisizioneFromExif);
        return {
            canRemove: hasWm,
            canAdd: !hasWm && !!media.dataAcquisizioneFromExif && media.dataAcquisizione != null,
        };
    }, [canReprocess, pollingMediaIds]);

    // Media selezionabili nella cartella e selezione divisa per azione, così ogni pulsante
    // agisce solo sui suoi. canDeleteMedia: solo media incarico con id numerico (esclusi
    // file ufficio e orfani); il BE decide se eliminare o chiedere approvazione in base al ruolo.
    const canDeleteMedia = useCallback((media: any): boolean => !media.isOffice && typeof media.id === 'number', []);

    const batchSel = useMemo(() => {
        if (viewData.type !== 'FILES') return { selectable: 0, selectableAny: 0, addIds: [] as number[], removeIds: [] as number[], deleteIds: [] as number[] };
        let selectable = 0, selectableAny = 0; const addIds: number[] = []; const removeIds: number[] = []; const deleteIds: number[] = [];
        for (const m of (viewData.items as any[])) {
            const o = evalMediaOps(m);
            const canWm = o.canAdd || o.canRemove;
            const canDel = canDeleteMedia(m);
            if (canWm) selectable++;
            if (canWm || canDel) selectableAny++;
            if (selectedMediaIds.has(m.id)) {
                if (o.canAdd) addIds.push(m.id);
                if (o.canRemove) removeIds.push(m.id);
                if (canDel) deleteIds.push(m.id);
            }
        }
        return { selectable, selectableAny, addIds, removeIds, deleteIds };
    }, [viewData, selectedMediaIds, evalMediaOps, canDeleteMedia]);

    const selectAllEligible = () => {
        if (viewData.type !== 'FILES') return;
        const ids = (viewData.items as any[])
            .filter(m => { const o = evalMediaOps(m); return o.canAdd || o.canRemove || canDeleteMedia(m); })
            .map(m => m.id);
        setSelectedMediaIds(new Set(ids));
    };

    // apply=true timbra la data, apply=false la rimuove. Agisce solo sui media idonei.
    const runBatchWatermark = async (apply: boolean) => {
        const ids = apply ? batchSel.addIds : batchSel.removeIds;
        if (ids.length === 0) return;
        const esclusi = selectedMediaIds.size - ids.length;
        const azione = apply ? 'Applicare la data (timbro)' : 'Rimuovere la data (timbro)';
        const ok = window.confirm(
            `${azione} su ${ids.length} file idone${ids.length === 1 ? 'o' : 'i'}?\n\n`
            + (esclusi > 0
                ? `${esclusi} dei selezionati non sono idonei e verranno ignorati `
                + `(${apply ? 'hanno già la data o non hanno una data EXIF reale' : 'non hanno una data da rimuovere'}).\n\n`
                : '')
            + `Il sistema riprocessa ogni file dall'originale. Operazione asincrona: il risultato appare dopo qualche secondo.`
        );
        if (!ok) return;
        setIsReprocessing(true);
        try {
            const res: any = await IncaricoService.reprocessMediaWatermark(ids, apply);
            const started = res?.data?.started ?? 0;
            const skipped = res?.data?.skippedIds?.length ?? 0;
            addToast(
                `${started} media in elaborazione${skipped > 0 ? ` · ${skipped} saltati (non idonei)` : ''}`,
                started > 0 ? 'success' : 'error',
            );
            clearSelection();
            if (onRefresh) onRefresh();
        } catch (e: any) {
            console.error('[runBatchWatermark]', e);
            addToast(e?.response?.data?.message || 'Errore avvio operazione', 'error');
        } finally {
            setIsReprocessing(false);
        }
    };

    // Eliminazione batch tramite il DELETE singolo (stessa logica per ruolo),
    // con al massimo 4 richieste in parallelo.
    const runBatchDelete = async () => {
        const ids = batchSel.deleteIds;
        if (ids.length === 0) return;
        const isPrivileged = user?.role === UserRole.ADMIN
            || (user?.role === UserRole.RESPONSABILE && responsabileDeleteDirettoAbilitato);
        const n = ids.length;
        const ok = window.confirm(isPrivileged
            ? `Eliminare definitivamente ${n} file selezionat${n === 1 ? 'o' : 'i'}? L'operazione non è reversibile.`
            : `Inviare al titolare la richiesta di eliminare ${n} file selezionat${n === 1 ? 'o' : 'i'}?`);
        if (!ok) return;
        setIsBatchDeleting(true);
        const settled = await mapWithConcurrency(ids, 4, (id) => IncaricoService.deleteMedia(id));
        const summary = { deleted: 0, requested: 0, errors: 0 };
        for (const r of settled) {
            if (r.status === 'rejected') { summary.errors++; continue; }
            if (classifyMediaDeleteResponse(r.value) === 'deleted') summary.deleted++;
            else summary.requested++;
        }
        setIsBatchDeleting(false);
        addToast(summarizeBatchDelete(summary),
            summary.errors && !summary.deleted && !summary.requested ? 'error' : 'success');
        clearSelection();
        onRefresh?.();
    };

    const MediaFileCard = ({ media }: { media: any }) => {
        const ext = (media.cleanFilename?.split('.').pop() || '').toLowerCase();
        // Tipi ricavati dall'estensione: i Files della pratica non hanno fileType.
        const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp'];
        const videoExts = ['mp4', 'mov', 'avi', 'mts', 'm2ts', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg'];
        const isImage = imageExts.includes(ext) || media.fileType?.startsWith('image');
        const isVideo = videoExts.includes(ext) || media.fileType?.includes('video');
        // Video ancora nel formato originale non riproducibile (es. .MTS): segnaposto invece di un player nero.
        const videoNotPlayable = isVideo && !isBrowserPlayableVideoUrl(media.displayUrl);
        const isPdf = ext === 'pdf' || media.fileType?.toLowerCase().includes('pdf');
        // È un documento "generico" solo se NON è image/video/pdf riconosciuto.
        const isDoc = media.isOffice && !isImage && !isVideo && !isPdf;
        // In elaborazione se la pipeline non ha ancora prodotto compressa né finale,
        // oppure se è in polling dopo un riprocessamento (il vecchio URL resta valido fino al callback).
        const isProcessing = !media.isOffice && (
            (!media.urlCompressed && !media.finalUrl) ||
            videoNotPlayable ||
            pollingMediaIds.has(media.id)
        );
        // Riprocessabili solo i media incarico foto/video già elaborati: la pipeline Python
        // gestisce solo questi. Selezionabile se idoneo ad almeno un'azione batch.
        const mediaOps = evalMediaOps(media);
        // Checkbox visibile se il file è idoneo ad almeno un'azione: data (watermark) o eliminazione.
        const canSelectForReprocess = mediaOps.canAdd || mediaOps.canRemove || canDeleteMedia(media);
        const isSelected = selectedMediaIds.has(media.id);
        return (
            <div className="flex flex-col gap-2">
                <div onClick={() => setPreviewFile(media)} className={`group relative aspect-square bg-slate-900 rounded-xl overflow-hidden border cursor-pointer shadow-sm hover:shadow-md transition-all ${isSelected ? 'border-blue-500 ring-2 ring-blue-300' : isProcessing ? 'border-amber-400 hover:border-amber-500' : 'border-slate-200 hover:border-blue-400'}`}>
                    {isPdf ? (
                        <PdfThumb
                            cacheKey={`${media.isOffice ? 'off' : 'med'}-${media.id}`}
                            getUrl={async () => {
                                const res = await (media.isOffice
                                    ? PraticaService.getDownloadUrl(media.id, true)
                                    : IncaricoService.getMediaUrl(media.id));
                                return extractUrl(res) || media.displayUrl;
                            }}
                        />
                    ) : isDoc ? <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100 gap-1"><FileText size={36} className="text-slate-400" /></div>
                        : isVideo ? (videoNotPlayable && !media.posterUrl
                            ? <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-slate-800 text-slate-300"><Video size={30} className="opacity-70" /><span className="text-[9px] font-black uppercase tracking-wider">.{ext || 'video'}</span></div>
                            : <div className="w-full h-full flex items-center justify-center relative">{media.posterUrl
                                ? <img src={media.posterUrl} alt={media.cleanFilename} className="w-full h-full object-cover opacity-70" loading="lazy" />
                                : <video src={media.displayUrl} preload="metadata" className="w-full h-full object-cover opacity-60" />}<PlayCircle className="text-white opacity-80 w-10 h-10 absolute" /></div>)
                            : <img src={media.displayUrl} alt={media.cleanFilename} className="w-full h-full object-cover opacity-90 group-hover:opacity-100" />}
                    <div className={`absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent transition-opacity p-3 flex flex-col justify-end ${(isPdf || isDoc) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        {/* Per i documenti ufficio il nome è sempre visibile: la miniatura non basta. */}
                        <p className="text-[10px] text-white font-bold truncate" title={media.cleanFilename}>{media.cleanFilename}</p>
                    </div>
                    {isProcessing && (
                        <div className="absolute top-2 left-2 flex items-center gap-1 bg-amber-500/90 backdrop-blur-sm text-white text-[9px] font-black px-2 py-1 rounded-lg pointer-events-none">
                            <Loader2 size={9} className="animate-spin shrink-0" /> ELABORAZIONE
                        </div>
                    )}
                    {/* Data proposta dal collaboratore in attesa di approvazione: finché non è risolta
                       il media resta senza data. */}
                    {media.pendingApprovalReason === 'MANUAL_DATE' && (
                        <div className="absolute top-2 left-2 flex items-center gap-1 bg-amber-500/90 backdrop-blur-sm text-white text-[9px] font-black px-2 py-1 rounded-lg pointer-events-none">
                            <Clock size={9} className="shrink-0" /> DATA IN APPROVAZIONE
                        </div>
                    )}
                    {/* Richiesta di eliminazione in attesa (pill viola come in PendingApprovalsView). */}
                    {media.pendingApprovalReason === 'DELETION_REQUEST' && (
                        <div className="absolute top-2 left-2 flex items-center gap-1 bg-purple-500/90 backdrop-blur-sm text-white text-[9px] font-black px-2 py-1 rounded-lg pointer-events-none">
                            <Clock size={9} className="shrink-0" /> ATTESA ELIMINAZIONE
                        </div>
                    )}
                    {/* Checkbox per la selezione batch, sempre visibile.
                       Resta tra le navigazioni nella cartella, si azzera cambiando cartella. */}
                    {canSelectForReprocess && (
                        <label
                            onClick={e => e.stopPropagation()}
                            className={`absolute top-2 left-2 flex items-center justify-center w-6 h-6 rounded-md cursor-pointer transition-all ${
                                isSelected
                                    ? 'bg-blue-600 shadow-lg'
                                    : 'bg-white/90 hover:bg-white border border-slate-300 opacity-70 hover:opacity-100'
                            }`}
                            title={isSelected ? 'Deseleziona' : (mediaOps.canAdd && mediaOps.canRemove ? 'Seleziona' : mediaOps.canAdd ? 'Seleziona (può aggiungere data)' : 'Seleziona (può togliere data)')}
                        >
                            <input
                                type="checkbox"
                                className="hidden"
                                checked={isSelected}
                                onChange={() => toggleSelection(media.id)}
                            />
                            {isSelected && <Check size={14} className="text-white" strokeWidth={3} />}
                        </label>
                    )}
                    {/* Toggle watermark per foto/video dell'incarico: solo titolare/responsabile, non per file
                       ufficio o media in elaborazione, e solo con data da EXIF (altrimenti si stamperebbe una data fittizia).
                       - "Rimuovi" se il watermark è applicato;
                       - "Applica" se non applicato e non già richiesto in upload;
                       - nessun bottone se richiesto ma non ancora applicato, per non suggerire un'azione inutile. */}
                    {(() => {
                        const baseOk = canToggleWatermark && !media.isOffice && (isImage || isVideo) && !isProcessing && media.dataAcquisizioneFromExif;
                        if (!baseOk) return null;
                        // Il watermark conta come applicato anche se richiesto in upload e il file è pronto:
                        // nei media in approvazione (es. DUPLICATE) il flag DB può non essere ancora aggiornato.
                        const showRemove = !!media.watermarkApplied || !!media.userWantsWatermark;
                        const showApply = !media.watermarkApplied && !media.userWantsWatermark;
                        if (!showRemove && !showApply) return null;
                        return (
                            <button
                                onClick={e => { e.stopPropagation(); handleToggleWatermark(media); }}
                                disabled={watermarkLoadingId === media.id}
                                className={`absolute top-2 right-2 p-1.5 rounded-lg transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-100 ${
                                    showRemove
                                        ? 'bg-amber-500/80 hover:bg-amber-600'
                                        : 'bg-blue-600/80 hover:bg-blue-700'
                                }`}
                                title={showRemove ? 'Rimuovi watermark' : 'Applica watermark'}
                            >
                                {watermarkLoadingId === media.id ? (
                                    <Loader2 size={11} className="text-white animate-spin" />
                                ) : showRemove ? (
                                    <Eraser size={11} className="text-white" />
                                ) : (
                                    <Stamp size={11} className="text-white" />
                                )}
                            </button>
                        );
                    })()}
                    {/* Eliminazione: l'admin elimina direttamente, il collaboratore invia una richiesta al titolare.
                       Il responsabile elimina direttamente solo con la deroga attiva in Settings. */}
                    {!media.isOffice && (
                        <button
                            onClick={async e => {
                                e.stopPropagation();
                                const isAdmin = user?.role === UserRole.ADMIN;
                                const isResponsabile = user?.role === UserRole.RESPONSABILE;
                                const isPrivileged = isAdmin || (isResponsabile && responsabileDeleteDirettoAbilitato);
                                const msg = isPrivileged
                                    ? `Eliminare definitivamente "${media.cleanFilename}"? L'operazione non è reversibile.`
                                    : `Inviare al titolare la richiesta di eliminare "${media.cleanFilename}"?`;
                                if (!window.confirm(msg)) return;
                                try {
                                    const res: any = await IncaricoService.deleteMedia(media.id);
                                    // Il backend risponde { direct, status }:
                                    // deleted          → eliminato direttamente
                                    // created          → nuova richiesta creata
                                    // already_pending  → richiesta già esistente
                                    const payload = res?.data?.data ?? res?.data;
                                    const status: string | undefined = payload?.status;
                                    const direct = payload?.direct === true || status === 'deleted';
                                    if (direct) {
                                        addToast('File eliminato', 'success');
                                    } else if (status === 'already_pending') {
                                        addToast('Richiesta già in attesa di approvazione', 'info');
                                    } else {
                                        addToast('Richiesta di eliminazione inviata al titolare', 'success');
                                    }
                                    if (direct || status === 'created') onRefresh?.();
                                } catch (err: any) {
                                    addToast(err?.response?.data?.message || "Errore nell'eliminazione", 'error');
                                }
                            }}
                            className="absolute bottom-2 right-2 bg-rose-700/80 hover:bg-rose-600 p-1.5 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            // "Elimina" solo per chi elimina direttamente, gli altri vedono la richiesta.
                            title={
                                user?.role === UserRole.ADMIN
                                || (user?.role === UserRole.RESPONSABILE && responsabileDeleteDirettoAbilitato)
                                    ? 'Elimina'
                                    : 'Richiedi eliminazione al titolare'
                            }
                        >
                            <Trash2 size={11} className="text-white" />
                        </button>
                    )}
                </div>
                <div className="px-1">
                    <p className="text-[10px] font-black text-slate-700 uppercase leading-none">{media.dateObj.toLocaleDateString('it-IT')}</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase">ore {media.dateObj.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
            </div>
        );
    };

    // Icona di stato nella lista upload
    const StatusIcon = ({ status }: { status: UploadStatus }) => {
        if (status === 'pending')    return <Circle size={16} className="text-slate-300" />;
        if (status === 'uploading')  return <Loader2 size={16} className="text-blue-500 animate-spin" />;
        if (status === 'ready')      return <CheckCircle2 size={16} className="text-emerald-500" />;
        if (status === 'needs_date') return <AlertTriangle size={16} className="text-amber-500" />;
        return <XCircle size={16} className="text-rose-500" />;
    };

    return (
        <div className="space-y-6 pb-10">
            {/* Breadcrumb */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex justify-between items-center">
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                    {pathSegments.map((seg, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <ChevronRight size={14} className="text-slate-300 shrink-0" />}
                            <button onClick={() => navigateBackTo(i)} className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${i === pathSegments.length - 1 ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
                                {seg === 'ROOT' ? 'ARCHIVIO' : seg}
                            </button>
                        </React.Fragment>
                    ))}
                </div>
                <button onClick={() => setIsUploadModalOpen(true)} className="bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-md shrink-0 ml-4">
                    <UploadCloud size={18} /> Aggiungi
                </button>
            </div>

            {/* File manager */}
            <div className="min-h-[400px]">
                {viewData.type === 'ROOT' && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                        {viewData.hasUfficio && (
                            <div onClick={() => navigateTo(UFFICIO_SEGMENT)} className="group bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-6 flex flex-col items-center gap-4 cursor-pointer hover:border-indigo-400 hover:shadow-lg transition-all">
                                <div className="p-4 bg-indigo-100 rounded-full group-hover:bg-indigo-200 transition-colors"><Briefcase className="text-indigo-600" size={24} /></div>
                                <span className="font-bold text-indigo-700 text-xs uppercase tracking-wider text-center">Documentazione Ufficio</span>
                            </div>
                        )}
                        {viewData.years.map((year: string) => <FolderCard key={year} name={year} icon={<Layers className="text-indigo-500" />} />)}
                        {!viewData.hasUfficio && viewData.years.length === 0 && (
                            <div className="col-span-full py-20 text-center text-slate-400 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                                <FolderOpen size={48} className="mx-auto mb-4 opacity-20" />Nessun media presente
                            </div>
                        )}
                    </div>
                )}
                {viewData.type === 'FOLDERS' && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                        {viewData.items.map((item: any) => <FolderCard key={item} name={item} icon={viewData.icon} />)}
                        {viewData.items.length === 0 && <div className="col-span-full py-20 text-center text-slate-400 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200"><FolderOpen size={48} className="mx-auto mb-4 opacity-20" />Nessuna cartella</div>}
                    </div>
                )}
                {viewData.type === 'FILES' && (
                    <>
                        {/* Barra selezione multipla (titolare/responsabile). "Seleziona tutto" prende gli idonei;
                           i pulsanti agiscono solo sul sottoinsieme idoneo, quindi non danno mai errore. */}
                        {batchSel.selectableAny > 0 && (
                            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-2xl shadow-sm px-4 py-3 flex flex-wrap items-center justify-between gap-3 sticky top-2 z-10">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={selectAllEligible}
                                        disabled={isReprocessing || isBatchDeleting}
                                        className="px-3 py-1.5 text-xs font-bold text-blue-700 bg-white border border-blue-200 hover:bg-blue-100 rounded-lg disabled:opacity-40 flex items-center gap-1.5"
                                    >
                                        <Check size={13} /> Seleziona tutto ({batchSel.selectableAny})
                                    </button>
                                    <span className="text-sm font-bold text-blue-800">
                                        {selectedMediaIds.size} selezionat{selectedMediaIds.size === 1 ? 'o' : 'i'}
                                    </span>
                                    {selectedMediaIds.size > 0 && (
                                        <button
                                            onClick={clearSelection}
                                            disabled={isReprocessing || isBatchDeleting}
                                            className="px-2 py-1.5 text-xs font-bold text-slate-600 hover:bg-white rounded-lg disabled:opacity-30"
                                        >
                                            Annulla
                                        </button>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    {canReprocess && (
                                        <>
                                            <button
                                                onClick={() => runBatchWatermark(true)}
                                                disabled={isReprocessing || isBatchDeleting || batchSel.addIds.length === 0}
                                                title={batchSel.addIds.length === 0 ? 'Nessun file selezionato idoneo (serve una data EXIF reale e nessun timbro)' : undefined}
                                                className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                {isReprocessing
                                                    ? <><Loader2 size={14} className="animate-spin" /> Avvio…</>
                                                    : <><Stamp size={14} /> Aggiungi data ({batchSel.addIds.length})</>}
                                            </button>
                                            <button
                                                onClick={() => runBatchWatermark(false)}
                                                disabled={isReprocessing || isBatchDeleting || batchSel.removeIds.length === 0}
                                                title={batchSel.removeIds.length === 0 ? 'Nessun file selezionato con una data da togliere' : undefined}
                                                className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white bg-amber-600 hover:bg-amber-700 rounded-lg shadow flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                {isReprocessing
                                                    ? <><Loader2 size={14} className="animate-spin" /> Avvio…</>
                                                    : <><Eraser size={14} /> Rimuovi data ({batchSel.removeIds.length})</>}
                                            </button>
                                        </>
                                    )}
                                    <button
                                        onClick={runBatchDelete}
                                        disabled={isReprocessing || isBatchDeleting || batchSel.deleteIds.length === 0}
                                        title={batchSel.deleteIds.length === 0 ? 'Nessun media eliminabile selezionato' : undefined}
                                        className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 rounded-lg shadow flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        {isBatchDeleting
                                            ? <><Loader2 size={14} className="animate-spin" /> Elimino…</>
                                            : <><Trash2 size={14} /> Elimina ({batchSel.deleteIds.length})</>}
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
                            {viewData.items.map((m: any) => <MediaFileCard key={m.id} media={m} />)}
                            {viewData.items.length === 0 && <div className="col-span-full py-20 text-center text-slate-400">Nessun file in questa cartella</div>}
                        </div>
                    </>
                )}
            </div>

            {/* KM */}
            {kmSessions.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                    <button onClick={() => setShowKmSection(v => !v)} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                            <div className="bg-amber-100 p-2 rounded-xl"><Car size={18} className="text-amber-600" /></div>
                            <div className="text-left">
                                <h3 className="text-sm font-black text-slate-800 uppercase tracking-wide">Documentazione Chilometrica</h3>
                                <p className="text-[10px] text-slate-400 font-bold">{kmSessions.reduce((acc, s) => acc + s.pairs.length, 0)} sessioni</p>
                            </div>
                        </div>
                        {showKmSection ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                    </button>
                    {showKmSection && (
                        <div className="border-t border-slate-100 divide-y divide-slate-50">
                            {kmSessions.map(({ incarico, collaboratoreName, pairs }) => (
                                <div key={incarico.id} className="p-4 space-y-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center"><User size={11} className="text-blue-600" /></div>
                                        <span className="text-[11px] font-black text-slate-700 uppercase tracking-wide">{collaboratoreName}</span>
                                        <span className="text-[9px] text-slate-400 font-bold ml-auto">Incarico #{incarico.id}</span>
                                    </div>
                                    {pairs.map((pair: any, idx: number) => (
                                        <div key={idx} className="bg-slate-50 rounded-xl p-3 space-y-2">
                                            <div className="flex items-center gap-1.5 text-[10px] font-black text-slate-500 uppercase">
                                                <Clock size={10} /> Sessione {idx + 1}
                                                {pair.dateInizio && <span className="ml-1 font-bold text-slate-400">· {pair.dateInizio.toLocaleDateString('it-IT')}</span>}
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                {[{ label: 'Inizio', colorClass: 'emerald', photo: pair.iniziale, date: pair.dateInizio }, { label: 'Fine', colorClass: 'rose', photo: pair.finale, date: pair.dateFine }].map(({ label, colorClass, photo, date }) => (
                                                    <div key={label} className="space-y-1.5">
                                                        <div className="flex items-center gap-1">
                                                            <div className={`w-2 h-2 rounded-full ${colorClass === 'emerald' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                                            <span className={`text-[9px] font-black uppercase tracking-wider ${colorClass === 'emerald' ? 'text-emerald-600' : 'text-rose-600'}`}>{label}</span>
                                                            {date && <span className="text-[8px] text-slate-400 ml-auto">{(date as Date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>}
                                                        </div>
                                                        {photo ? (
                                                            <div onClick={() => setPreviewFile({ ...photo, isOffice: false, displayUrl: photo.urlCompressed || photo.finalUrl, dateObj: date || new Date(), cleanFilename: photo.filename, uploaderName: collaboratoreName })}
                                                                className={`aspect-video max-w-[180px] rounded-lg overflow-hidden bg-slate-800 cursor-pointer transition-all ${colorClass === 'emerald' ? 'hover:ring-2 hover:ring-emerald-400' : 'hover:ring-2 hover:ring-rose-400'}`}>
                                                                <img src={photo.urlCompressed || photo.finalUrl} alt={label} className="w-full h-full object-cover" loading="lazy" />
                                                            </div>
                                                        ) : (
                                                            <div className="aspect-video max-w-[180px] rounded-lg bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-200">
                                                                <span className="text-[9px] text-slate-400 font-bold">Mancante</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Lightbox */}
            {previewFile && (
                <Modal title={previewFile.isOffice ? 'File Ufficio' : 'Acquisizione Collaboratore'} onClose={() => setPreviewFile(null)}>
                    <div className="flex flex-col items-center bg-slate-900 -mx-5 -mb-5 p-6 rounded-b-xl min-h-[400px]">
                        {isLoadingPreview ? <Loader2 className="animate-spin text-blue-500 mt-20" size={48} /> : (
                            <div className="w-full flex flex-col items-center gap-6">
                                {(() => {
                                    const url = freshPreviewUrl || previewFile.displayUrl || previewFile.urlOriginal;
                                    const name = (previewFile.cleanFilename || previewFile.name || '').toLowerCase();
                                    const type = (previewFile.fileType || '').toLowerCase();
                                    const ext = name.split('.').pop() || '';
                                    const isVideo = type.includes('video')
                                        || ['mp4', 'mov', 'avi', 'mts', 'm2ts', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg'].includes(ext);
                                    const isPdf = type.includes('pdf') || name.endsWith('.pdf');
                                    if (isVideo && (previewVideoError || !isBrowserPlayableVideoUrl(url))) {
                                        // Originale non riproducibile (es. .MTS) e copia mp4 non ancora pronta: messaggio invece di un player nero.
                                        return (
                                            <div className="w-full max-w-lg flex flex-col items-center text-center gap-3 py-16 px-6 rounded-xl bg-slate-800 text-slate-200">
                                                <Video size={44} className="text-amber-400" />
                                                <p className="text-sm font-black">Anteprima video non disponibile</p>
                                                <p className="text-xs text-slate-400 leading-relaxed">
                                                    {previewVideoError
                                                        ? 'Il browser non riesce a riprodurre questo video.'
                                                        : `Il formato originale (.${(ext || '').toUpperCase()}) non è riproducibile nel browser e la versione convertita (MP4) non è ancora pronta.`}
                                                    {' '}Riprova tra qualche minuto oppure scarica l'originale.
                                                </p>
                                            </div>
                                        );
                                    }
                                    if (isVideo) return <video src={url} poster={previewFile.posterUrl || undefined} controls autoPlay onError={() => setPreviewVideoError(true)} className="w-full max-h-[60vh] rounded-xl object-contain bg-black shadow-2xl" />;
                                    if (isPdf) return <iframe src={url} title={previewFile.cleanFilename || 'PDF'} className="w-full h-[70vh] rounded-xl bg-white shadow-2xl" />;
                                    if (isConvertibleImageExt(ext) && !isBrowserImageUrl(url)) {
                                        // RAW/HEIC senza copia JPG pronta: il browser non apre l'originale.
                                        return (
                                            <div className="w-full max-w-lg flex flex-col items-center text-center gap-3 py-16 px-6 rounded-xl bg-slate-800 text-slate-200">
                                                <ImageIcon size={44} className="text-amber-400" />
                                                <p className="text-sm font-black">Anteprima non ancora pronta</p>
                                                <p className="text-xs text-slate-400 leading-relaxed">
                                                    {`Il browser non apre il formato .${(ext || '').toUpperCase()} e la copia per l'anteprima non è ancora pronta.`}
                                                    {' '}Riprova tra qualche minuto oppure scarica il file.
                                                </p>
                                            </div>
                                        );
                                    }
                                    return <img src={url} alt="Preview" className="w-full max-h-[60vh] rounded-xl object-contain shadow-2xl" />;
                                })()}
                                <div className="w-full bg-slate-800 p-4 rounded-xl border border-slate-700 flex justify-between items-center text-white">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-sm font-bold truncate">{previewFile.cleanFilename}</span>
                                        <span className="text-[10px] text-slate-400 flex items-center gap-1">
                                            <Camera size={10} /> {previewFile.uploaderName} • {previewFile.dateObj.toLocaleString('it-IT')}
                                        </span>
                                        {!previewFile.isOffice && !previewFile.urlCompressed && !previewFile.finalUrl && (
                                            <span className="text-[10px] text-amber-400 font-black flex items-center gap-1">
                                                <Loader2 size={10} className="animate-spin shrink-0" /> Elaborazione in corso · stai vedendo l'originale
                                            </span>
                                        )}
                                    </div>
                                    <button onClick={async () => {
                                        // Link in attachment chiesto al clic: l'URL dell'anteprima è inline e
                                        // aprirebbe il file in una nuova pagina. I file ufficio scaricano
                                        // l'originale, i media la stessa copia mostrata in anteprima.
                                        try {
                                            let dl: string | undefined;
                                            if (previewFile.isOffice) {
                                                const res: any = await PraticaService.getDownloadUrl(previewFile.id);
                                                dl = res?.url || res?.data?.url;
                                            } else {
                                                dl = await IncaricoService.getMediaUrl(previewFile.id, true);
                                            }
                                            if (dl) { downloadAttachmentUrl(dl); return; }
                                        } catch { /* fallback sotto */ }
                                        window.open(freshPreviewUrl || previewFile.displayUrl, '_blank');
                                    }} className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 shrink-0 ml-4">
                                        <Download size={16} /> Scarica
                                    </button>
                                    {!previewFile.isOffice && copiaInAltroFormato(previewFile.cleanFilename, freshPreviewUrl || previewFile.displayUrl) && (
                                        // "Scarica" dà la copia convertita (es. JPG di un .CR2): qui il file caricato.
                                        <button onClick={async () => {
                                            try {
                                                const dl = await IncaricoService.getMediaUrl(previewFile.id, true, true);
                                                if (dl) downloadAttachmentUrl(dl);
                                            } catch { /* nessun link: niente da scaricare */ }
                                        }} className="bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 shrink-0 ml-2">
                                            <Download size={16} /> Scarica originale
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {/* Modale riepilogo upload */}
            <UploadSummaryModal
                isOpen={showSummary}
                onClose={() => { setShowSummary(false); onRefresh?.(); }}
                results={summaryResults}
                incaricoId={summaryIncaricoId}
                context="admin"
                onPollStatus={IncaricoService.getMediaAnalysisStatus}
                onManualDateSave={async (mediaId, tempId, date, time) => {
                    const iso = `${date}T${time}:00`;
                    const res: any = await IncaricoService.confirmDate(tempId, iso);
                    // Collaboratore → richiesta di approvazione; admin/responsabile → salvataggio diretto.
                    if (res?.data?.direct === false) {
                        const msg = res?.data?.status === 'already_pending'
                            ? 'Richiesta già in attesa di approvazione'
                            : 'Data inviata al titolare per approvazione';
                        addToast(msg, 'info');
                    } else {
                        addToast('Data salvata', 'success');
                    }
                }}
                onDiscard={async (_mediaId, tempId) => {
                    await IncaricoService.deleteMediaByTempId(tempId);
                    addToast('File annullato', 'info');
                }}
                onKeep={async (mediaId) => {
                    await IncaricoService.keepMedia(mediaId);
                    addToast('File mantenuto', 'success');
                }}
            />

            {/* Modale upload */}
            {isUploadModalOpen && (
                <Modal
                    title={
                        modalStep === 'completed' ? 'Caricamento completato'
                        : modalStep === 'uploading' ? `Caricamento... ${uploadProgress}%`
                        : modalStep === 'confirm_dates' ? 'Date mancanti'
                        : 'Carica Media'
                    }
                    onClose={
                        modalStep === 'uploading' ? undefined
                        : modalStep === 'confirm' ? discardOrphansAndClose
                        : modalStep === 'completed' ? closeCompleted
                        : closeModal
                    }
                >
                    {/* Step 1: selezione */}
                    {modalStep === 'select' && (
                        <div className="space-y-5">
                            <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-xl">
                                {(['ufficio', 'media'] as const).map(t => (
                                    <button key={t} type="button" onClick={() => setUploadType(t)}
                                        className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${uploadType === t ? 'bg-white shadow text-blue-700' : 'text-slate-500'}`}>
                                        {t === 'ufficio' ? <><Briefcase size={16} /> Documentazione Ufficio</> : <><Camera size={16} /> Media</>}
                                    </button>
                                ))}
                            </div>

                            {uploadType === 'media' && soggettiList.length > 1 && (
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                                        Soggetto investigato <span className="text-rose-500">*</span>
                                    </label>
                                    <select
                                        value={uploadSoggetto}
                                        onChange={e => setUploadSoggetto(e.target.value)}
                                        className="w-full text-sm p-2.5 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="">— Seleziona soggetto —</option>
                                        {soggettiList.map(s => (
                                            <option key={s.nome} value={s.nome}>{s.nome}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {uploadType === 'media' && (
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Dispositivo</label>
                                    <div className="grid grid-cols-5 gap-2">
                                        {DEVICE_OPTIONS.map(opt => {
                                            const Icon = opt === 'VIDEOCAMERA' ? Video : Camera;
                                            return (
                                                <button key={opt} type="button" onClick={() => setUploadCategoria(opt)}
                                                    className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 text-[10px] font-black uppercase transition-all ${uploadCategoria === opt ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                                                    <Icon size={18} />{opt}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">File</label>
                                <label className="border-2 border-dashed border-blue-200 rounded-2xl p-6 text-center bg-blue-50/50 cursor-pointer flex flex-col items-center gap-2 hover:bg-blue-50 transition-colors">
                                    <UploadCloud className="text-blue-400" size={28} />
                                    <span className="text-xs font-bold text-slate-600">Trascina o clicca per selezionare</span>
                                    {uploadItems.length > 0 && <span className="text-xs font-black text-blue-600">{uploadItems.length} file selezionati</span>}
                                    <input type="file" multiple className="hidden" onChange={e => {
                                        if (!e.target.files) return;
                                        setUploadItems(Array.from(e.target.files).map(file => ({ file, status: 'pending', manualDate: '' })));
                                    }} />
                                </label>
                            </div>

                            <div className="flex justify-end gap-2 pt-1">
                                <button onClick={closeModal} className="px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-lg">Annulla</button>
                                <button
                                    onClick={() => setModalStep('confirm')}
                                    disabled={uploadItems.length === 0 || (uploadType === 'media' && soggettiList.length > 1 && !uploadSoggetto)}
                                    className="bg-blue-600 text-white px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50 shadow-md">
                                    <UploadCloud size={18} /> Carica {uploadItems.length > 0 ? `${uploadItems.length} file` : ''}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step conferma */}
                    {modalStep === 'confirm' && (
                        <div className="space-y-5">
                            <div className="flex flex-col items-center text-center gap-4 py-3">
                                <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
                                    <UploadCloud size={28} className="text-amber-500" />
                                </div>
                                <div>
                                    <p className="font-black text-slate-900 text-base">Conferma caricamento</p>
                                    <p className="text-sm text-slate-500 mt-1.5">
                                        Stai per caricare{' '}
                                        <span className="font-black text-slate-800">{uploadItems.length} {uploadItems.length === 1 ? 'file' : 'file'}</span>
                                        {' '}per il mandato
                                    </p>
                                    <p className="font-black text-blue-600 mt-1 text-sm truncate max-w-xs mx-auto">{pratica.name}</p>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        {uploadType === 'ufficio' ? 'Documentazione Ufficio' : uploadCategoria}
                                        {uploadType === 'media' && uploadSoggetto && (
                                            <span className="ml-1 font-bold text-slate-600">· {uploadSoggetto}</span>
                                        )}
                                    </p>
                                </div>
                            </div>

                            {/* Scelta watermark, solo per i media (non per la documentazione ufficio).
                               Vale per tutto il batch, default attivo. */}
                            {uploadType === 'media' && (
                                <label className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-2xl cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={applyDateWatermark}
                                        onChange={e => setApplyDateWatermark(e.target.checked)}
                                        className="mt-0.5 w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300 cursor-pointer"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[11px] font-black text-slate-700 uppercase tracking-tight">Applica data come watermark</p>
                                        <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                                            Stampa la data di acquisizione (EXIF) sull'immagine. Disattiva se la foto la contiene già.
                                        </p>
                                    </div>
                                </label>
                            )}

                            <div className="flex gap-2 pt-1">
                                <button onClick={() => setModalStep('select')} className="flex-1 px-4 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-xl border border-slate-200">
                                    Annulla
                                </button>
                                <button onClick={startUpload} className="flex-1 bg-blue-600 text-white px-6 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-md">
                                    <UploadCloud size={18} /> Conferma
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 2: caricamento */}
                    {modalStep === 'uploading' && (
                        <div className="space-y-4">
                            {/* Barra progresso */}
                            <div className="w-full bg-slate-100 rounded-full h-2">
                                <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                            </div>
                            {(() => {
                                const done = uploadItems.filter(i => ['ready','needs_date','error'].includes(i.status)).length;
                                const uploadedMB = (uploadedBytes / 1048576).toFixed(1);
                                const totalMB = (uploadTotalBytes / 1048576).toFixed(1);
                                const remainingB = Math.max(0, uploadTotalBytes - uploadedBytes);
                                const etaSec = uploadSpeedMBps && uploadSpeedMBps > 0.01
                                    ? remainingB / (uploadSpeedMBps * 1048576) : null;
                                const etaStr = etaSec !== null
                                    ? etaSec < 60 ? `~${Math.round(etaSec)}s` : `~${Math.floor(etaSec / 60)}m ${Math.round(etaSec % 60)}s`
                                    : null;
                                return (
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500 font-medium">
                                            {done}/{uploadItems.length} file
                                            {uploadTotalBytes > 0 && <span className="text-slate-400"> · {uploadedMB} / {totalMB} MB</span>}
                                        </span>
                                        <span className="font-black text-blue-600 tabular-nums">
                                            {uploadSpeedMBps !== null && `${uploadSpeedMBps.toFixed(1)} MB/s`}
                                            {etaStr && <span className="text-slate-400 font-medium"> · {etaStr}</span>}
                                        </span>
                                    </div>
                                );
                            })()}

                            {/* Lista file */}
                            <div className="space-y-1.5 max-h-80 overflow-y-auto">
                                {uploadItems.map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-200">
                                        <StatusIcon status={item.status} />
                                        <span className="text-xs font-medium text-slate-700 truncate flex-1">{item.file.name}</span>
                                        <span className="text-[10px] font-bold shrink-0 ml-2">
                                            {item.status === 'ready' && item.detectedDate && <span className="text-emerald-600">{new Date(item.detectedDate).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
                                            {item.status === 'needs_date' && <span className="text-amber-600">Data mancante</span>}
                                            {item.status === 'error' && <span className="text-rose-600">{item.error || 'Errore'}</span>}
                                            {item.status === 'uploading' && <span className="text-blue-500">Caricamento...</span>}
                                            {item.status === 'pending' && <span className="text-slate-400">In attesa</span>}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 3: date mancanti */}
                    {modalStep === 'confirm_dates' && (
                        <div className="space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
                                <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                                <p className="text-sm text-amber-800 font-medium">
                                    {uploadItems.filter(i => i.status === 'needs_date').length} file non hanno la data EXIF. Inserire la data di acquisizione reale.
                                </p>
                            </div>

                            <div className="space-y-3 max-h-72 overflow-y-auto">
                                {uploadItems.map((item, i) => item.status !== 'needs_date' ? null : (
                                    <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                                        <p className="text-xs font-bold text-slate-700 mb-2 truncate">{item.file.name}</p>
                                        <input
                                            type="datetime-local"
                                            className="w-full text-sm p-2 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                            value={item.manualDate}
                                            onChange={e => updateItem(i, { manualDate: e.target.value })}
                                        />
                                    </div>
                                ))}
                            </div>

                            {/* Riepilogo file già ok */}
                            {uploadItems.some(i => i.status === 'ready') && (
                                <p className="text-xs text-slate-400 text-center">
                                    + {uploadItems.filter(i => i.status === 'ready').length} file già confermati con data EXIF
                                </p>
                            )}

                            <div className="flex justify-end gap-2 pt-1">
                                <button onClick={discardOrphansAndClose} className="px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-lg">Annulla</button>
                                <button
                                    onClick={confirmDates}
                                    disabled={isConfirming || uploadItems.filter(i => i.status === 'needs_date').some(i => !i.manualDate)}
                                    className="bg-blue-600 text-white px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50 shadow-md"
                                >
                                    {isConfirming ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} />}
                                    {isConfirming ? 'Conferma...' : 'Conferma e Termina'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 4: completato */}
                    {modalStep === 'completed' && completedSummary && (
                        <div className="space-y-5">
                            <div className="flex flex-col items-center text-center gap-4 py-3">
                                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                                    <CheckCircle2 size={28} className="text-emerald-500" />
                                </div>
                                <div>
                                    <p className="font-black text-slate-900 text-base">Caricamento completato</p>
                                    <p className="text-sm text-slate-500 mt-1.5">
                                        <span className="font-black text-emerald-600">{completedSummary.ok}</span> file caricati con successo
                                    </p>
                                    {completedSummary.errors > 0 && (
                                        <p className="text-xs text-rose-600 mt-1 font-bold">{completedSummary.errors} errori</p>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={closeCompleted}
                                className="w-full bg-emerald-600 text-white px-6 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-md"
                            >
                                OK
                            </button>
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
};
